/**
 * @file PdfCanvasPreview.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-09-30
 * @description A PDF shown as pictures of its pages — no viewer toolbar, so
 *              nothing to save or print it from. Anything done with a document
 *              goes through the system's own Print / Download / Email, which
 *              are logged. (Nothing on screen can stop a screenshot; this is
 *              about making the logged way the only built-in one.)
 *
 * Pages are drawn with pdf.js onto canvases, lazily loaded so the library is
 * only fetched when a preview opens. It runs on the page's own thread (the
 * "fake worker"), which needs no worker file or CSP change and is quick
 * enough for a few pages of an invoice or PO. The right-click menu is off on
 * the pages so "Save image as…" is not offered either.
 *
 * Props: `load` — () => Promise<ArrayBuffer>, re-run when `loadKey` changes.
 */

import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinner from './LoadingSpinner';
import './PdfCanvasPreview.css';

let pdfjsPromise = null;
function pdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import('pdfjs-dist/build/pdf'),
      // Registers the worker code on the page, so pdf.js runs without a
      // separate worker script.
      import('pdfjs-dist/build/pdf.worker.entry')
    ]).then(([lib]) => lib);
  }
  return pdfjsPromise;
}

function PdfCanvasPreview({ load, loadKey, scale = 1.4, className = '' }) {
  const hostRef = useRef(null);
  const [state, setState] = useState({ loading: true, error: null, pages: 0 });

  useEffect(() => {
    let cancelled = false;
    let doc = null;
    const host = hostRef.current;
    if (host) host.innerHTML = '';
    setState({ loading: true, error: null, pages: 0 });

    (async () => {
      try {
        const [lib, data] = await Promise.all([pdfjs(), load()]);
        if (cancelled) return;
        doc = await lib.getDocument({ data: new Uint8Array(data) }).promise;
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return;
          const page = await doc.getPage(n);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.className = 'pdf-canvas-page';
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.oncontextmenu = (e) => e.preventDefault();
          host?.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
        if (!cancelled) setState({ loading: false, error: null, pages: doc.numPages });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.message || 'Could not show the preview', pages: 0 });
      }
    })();

    return () => {
      cancelled = true;
      if (doc) doc.destroy().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  return (
    <div className={`pdf-canvas-preview ${className}`.trim()} onContextMenu={(e) => e.preventDefault()}>
      {state.loading && <div className="pdf-canvas-status"><LoadingSpinner /></div>}
      {state.error && <div className="pdf-canvas-status pdf-canvas-error">{state.error}</div>}
      <div ref={hostRef} className="pdf-canvas-pages" />
    </div>
  );
}

export default PdfCanvasPreview;
